import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType, Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import {
  apps,
  sourceAccountId,
  dataClasses,
  environment,
  resourceName,
  resourceType,
  sensitivityAnnotations,
  sourceRegion,
  subnets,
  vpc,
  resourceId,
  cloudProvider,
  resourceGroupName,
  tagKeys,
  tagKeyValues,
  sourceAccountTagKeys,
  sourceAccountTagKeyValues,
  globalClusterIdentifier,
} from '@/components/queryBuilder/properties';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { FilterProperty } from '@/types/advanceFilter';

const baseProperties: FilterProperty[] = [
  resourceType,
  dataClasses,
  environment,
  apps,
];

const propertiesWithSensitivityAnnotations: FilterProperty[] = [
  ...baseProperties,
  sensitivityAnnotations,
];

const sourceLocationProperties: FilterProperty[] = [
  cloudProvider,
  sourceAccountId,
  sourceRegion,
  vpc,
  subnets,
  resourceGroupName,
  sourceAccountTagKeys,
  sourceAccountTagKeyValues,
];

const sourceLocationPropertiesWithGlobalClusterIdentifier: FilterProperty[] = [
  ...sourceLocationProperties,
  globalClusterIdentifier,
];

const resourceIdentifierProperties: FilterProperty[] = [
  resourceName,
  resourceId,
];

const tagProperties: FilterProperty[] = [tagKeys, tagKeyValues];

export const useAllBackupPolicyProperties = (): FilterProperty[][] => {
  const {
    SensitivityAnnotations: enableSensitivityAnnotations,
    AuroraGlobalDbDiscoverySupport: enableAuroraGlobalDbDiscoverySupport,
  } = useFeatureFlags();

  return useMemo(
    () => [
      enableSensitivityAnnotations
        ? propertiesWithSensitivityAnnotations
        : baseProperties,
      enableAuroraGlobalDbDiscoverySupport
        ? sourceLocationPropertiesWithGlobalClusterIdentifier
        : sourceLocationProperties,
      resourceIdentifierProperties,
      tagProperties,
    ],
    [enableSensitivityAnnotations, enableAuroraGlobalDbDiscoverySupport]
  );
};

const awsPolicySupportedProperties = new Set<FilterProperty>([
  resourceType,
  dataClasses,
  environment,
  apps,
  sensitivityAnnotations,
  sourceAccountId,
  sourceRegion,
  vpc,
  subnets,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
  globalClusterIdentifier,
]);

const gcpPolicySupportedProperties = new Set<FilterProperty>([
  resourceType,
  dataClasses,
  environment,
  apps,
  sensitivityAnnotations,
  sourceAccountId,
  sourceRegion,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
  sourceAccountTagKeys,
  sourceAccountTagKeyValues,
]);

const azurePolicySupportedProperties = new Set<FilterProperty>([
  resourceType,
  dataClasses,
  environment,
  apps,
  sensitivityAnnotations,
  sourceAccountId,
  sourceRegion,
  resourceGroupName,
  resourceName,
  resourceId,
  tagKeys,
  tagKeyValues,
]);

const cloudPropsMap: Record<Provider, Set<FilterProperty>> = {
  [Provider.Aws]: awsPolicySupportedProperties,
  [Provider.Gcp]: gcpPolicySupportedProperties,
  [Provider.Azure]: azurePolicySupportedProperties,
  [Provider.MongoAtlas]: new Set(),
  [Provider.GoogleWorkspace]: new Set(),
  [Provider.Microsoft365]: new Set(),
  [Provider.ProviderUnspecified]: new Set(),
};

// Properties not supported by AWS Native Standard resources (EFS, FSX,
// DocumentDB, Neptune, RDS): these resource types have no application
// detection, no data classification, no Azure MIP sensitivity annotations,
// and no Aurora global cluster identifier when in an AWS_NATIVE_STANDARD policy.
const awsNativeStandardUnsupportedProperties = new Set<FilterProperty>([
  apps,
  dataClasses,
  sensitivityAnnotations,
  globalClusterIdentifier,
]);

const azureFilesUnsupportedProperties = new Set<FilterProperty>([
  apps,
  dataClasses,
  sensitivityAnnotations,
]);

// Azure SQL Database native PITR only enforces the database's Azure short-term
// retention window — Eon does not scan or copy these databases, so there is no
// application detection, data classification, or sensitivity annotation to filter on.
const azureSqlDatabaseNativePitrUnsupportedProperties = new Set<FilterProperty>(
  [apps, dataClasses, sensitivityAnnotations]
);

export const useBackupPolicySupportedProperties = (
  supportedResourceTypes: ResourceType[],
  sourceProviders?: Provider[],
  policyType?: BackupPolicyType
): FilterProperty[][] => {
  const properties = useAllBackupPolicyProperties();

  return useMemo(() => {
    if (!sourceProviders?.length) {
      return properties;
    }

    return properties.map((group) =>
      group.filter((prop) => {
        if (
          policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD &&
          awsNativeStandardUnsupportedProperties.has(prop)
        ) {
          return false;
        }

        if (
          policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES &&
          azureFilesUnsupportedProperties.has(prop)
        ) {
          return false;
        }

        if (
          policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR &&
          azureSqlDatabaseNativePitrUnsupportedProperties.has(prop)
        ) {
          return false;
        }

        return sourceProviders.some((cloud) => {
          if (prop.name === resourceType.name) {
            return supportedResourceTypes.length > 1;
          } else if (prop.name === cloudProvider.name) {
            return sourceProviders.length > 1;
          } else {
            return cloudPropsMap[cloud].has(prop);
          }
        });
      })
    );
  }, [sourceProviders, supportedResourceTypes, properties, policyType]);
};
